import { useCallback } from 'react';
import type { PaywallFeature } from '../gates/gates';
import {
  getCancelPath,
  getRedirectUrl,
  PLUS_ANNUAL,
} from '../lib/getRedirectUrl';
import type {
  GetRedirectUrlParams,
  PackageId,
  SubscriptionType,
} from '../types';

export const useBilling = () => {
  // The server owns the flow for a package: a Family checkout is turned into
  // an in-place upgrade or an addon purchase there, so one entry point per
  // package is all the client needs.
  const redirectToCheckout = useCallback(
    async (
      subscriptionType: SubscriptionType,
      redirectOptions: GetRedirectUrlParams = {},
      packageId: PackageId = PLUS_ANNUAL,
      options: { packs?: number; feature?: PaywallFeature } = {}
    ) => {
      try {
        const redirectPath = getRedirectUrl(redirectOptions);
        // Basic click tracking: default to the launch key, otherwise the current path.
        const source = redirectOptions.launch ?? window.location.pathname;
        // Cancelling or clicking back in Stripe should return the user to where
        // they were rather than dropping them on the dashboard.
        const cancelPath = getCancelPath();

        const params = new URLSearchParams({
          packageId,
          source,
          cancelPath,
          ...(redirectPath && { redirectPath }),
          // Business packages only: seat packs picked before checkout.
          ...(options.packs ? { packs: String(options.packs) } : {}),
          // The paywall gate behind this checkout. `source` can't identify it:
          // gates without a launch key fall back to the pathname, and several
          // share one key. The server stamps it on the purchase event.
          ...(options.feature && { feature: options.feature }),
        });
        window.location.href = `/billing/checkout?${params}`;
      } catch (error) {
        console.error('Failed to redirect to checkout:', error);
      }
    },
    []
  );

  const redirectToBillingPortal = useCallback(async () => {
    try {
      window.open(`/billing/portal`, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to redirect to billing portal:', error);
    }
  }, []);

  return {
    redirectToCheckout,
    redirectToBillingPortal,
  };
};
